import {
  OWASP_RISK_FACTOR_KEYS,
  parseOwaspRiskVector,
  type OwaspRiskFactorKey,
} from '@terra/shared-cvss';
import { isObject } from 'radash';

export type OwaspFactorChange = {
  field: OwaspRiskFactorKey;
  beforeLabel: string;
  afterLabel: string;
};

type OwaspRiskValue = {
  score?: string | null;
  vector?: string | null;
  level?: string | null;
};

function readOwaspRiskValue(value: unknown): OwaspRiskValue | null {
  if (!isObject(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const score = typeof record.score === 'string' ? record.score : null;
  const vector = typeof record.vector === 'string' ? record.vector : null;
  const level = typeof record.level === 'string' ? record.level : null;

  if (!score && !vector && !level) {
    return null;
  }

  return { score, vector, level };
}

function readOwaspVector(value: unknown): string {
  const risk = readOwaspRiskValue(value);

  return risk?.vector?.trim() ?? '';
}

export function getOwaspFactorChanges({
  before,
  after,
}: {
  before: unknown;
  after: unknown;
}): OwaspFactorChange[] {
  const beforeFactors = parseOwaspRiskVector(readOwaspVector(before));
  const afterFactors = parseOwaspRiskVector(readOwaspVector(after));

  if (!beforeFactors || !afterFactors) {
    return [];
  }

  return OWASP_RISK_FACTOR_KEYS.flatMap((field) => {
    const beforeValue = beforeFactors[field];
    const afterValue = afterFactors[field];

    if (beforeValue === afterValue) {
      return [];
    }

    return [
      {
        field,
        beforeLabel: String(beforeValue),
        afterLabel: String(afterValue),
      },
    ];
  });
}

export function getOwaspScoreTransition({
  before,
  after,
}: {
  before: unknown;
  after: unknown;
}): { beforeLabel: string; afterLabel: string } | null {
  const beforeRisk = readOwaspRiskValue(before);
  const afterRisk = readOwaspRiskValue(after);
  const beforeScore = beforeRisk?.score?.trim() ?? '';
  const afterScore = afterRisk?.score?.trim() ?? '';

  if (!beforeScore && !afterScore) {
    return null;
  }

  if (beforeScore === afterScore) {
    return null;
  }

  return {
    beforeLabel: beforeScore || '-',
    afterLabel: afterScore || '-',
  };
}

export function hasOwaspVectorChanged({
  before,
  after,
}: {
  before: unknown;
  after: unknown;
}): boolean {
  const beforeVector = readOwaspVector(before);
  const afterVector = readOwaspVector(after);

  return Boolean(beforeVector && afterVector && beforeVector !== afterVector);
}

function scoresDiffer(before: unknown, after: unknown): boolean {
  const beforeScore = readOwaspRiskValue(before)?.score?.trim() ?? '';
  const afterScore = readOwaspRiskValue(after)?.score?.trim() ?? '';

  return Boolean(beforeScore && afterScore && beforeScore !== afterScore);
}

export function resolveOwaspSuggestionBefore({
  pendingBefore,
  pendingAfter,
  original,
}: {
  pendingBefore: unknown;
  pendingAfter: unknown;
  original?: unknown;
}): unknown {
  if (
    hasOwaspVectorChanged({ before: pendingBefore, after: pendingAfter }) ||
    scoresDiffer(pendingBefore, pendingAfter)
  ) {
    return pendingBefore;
  }

  if (
    original !== undefined &&
    (hasOwaspVectorChanged({ before: original, after: pendingAfter }) ||
      scoresDiffer(original, pendingAfter))
  ) {
    return original;
  }

  return pendingBefore;
}
